import * as ex from 'excalibur';
import { ActionType, Allegiance, ControlType } from '../types/enums';
import { Action } from '../types/interfaces';
import { TILE_SIZE, DIRECTIONS, GRID_WIDTH, GRID_HEIGHT } from '../config/constants';
import { Unit } from '../core/unit';
import { Grid } from '../core/grid';
import { PlayerUnit } from './playerUnit';

// Imp unit - hops over tiles in cardinal directions
export class Imp extends Unit {
  constructor(options: {
    gridX: number,
    gridY: number,
    allegiance: Allegiance,
    initiative: number,
    controlType?: ControlType,
    grid: Grid
  }) {
    super({
      pos: new ex.Vector(options.gridX * TILE_SIZE + TILE_SIZE / 2, options.gridY * TILE_SIZE + TILE_SIZE / 2),
      gridX: options.gridX,
      gridY: options.gridY,
      type: 'imp',
      name: 'Imp',
      width: TILE_SIZE * 0.65,
      height: TILE_SIZE * 0.65,
      color: options.allegiance === Allegiance.Player ? ex.Color.Green : ex.Color.Purple,
      initiative: options.initiative,
      allegiance: options.allegiance,
      health: 20,
      maxHealth: 20,
      controlType: options.controlType || (options.allegiance === Allegiance.Player ?
        ControlType.Manual : ControlType.Automatic),
      moveRange: 2,
      attackRange: 1,
      abilities: ['hop_over'],
      grid: options.grid
    });

    // Set the emoji for this unit type
    this.setEmoji("🏃"); // Runner for scout/fast unit
  }

  public decideAction(): Action | null {
    // If manual control and player unit, delegate to player input
    if (this.controlType === ControlType.Manual && this instanceof PlayerUnit) {
      return super.decideAction();
    }

    // Check for enemies in attack range
    const attackTargets = this.findTargetsInRange(this.attackRange);
    if (attackTargets.length > 0) {
      // Attack the first enemy in range
      return this.createAttackAction(attackTargets[0]);
    }

    // Try to hop toward enemies (Imps try to hop over obstacles)
    const closestEnemy = this.findClosestEnemy();
    if (closestEnemy) {
      // Calculate direction to enemy
      const dx = closestEnemy.gridX - this.gridX;
      const dy = closestEnemy.gridY - this.gridY;

      // Try to move in the most direct direction first
      const directionPriority = [];
      if (Math.abs(dx) > Math.abs(dy)) {
        // Prioritize horizontal movement
        directionPriority.push(
          { x: Math.sign(dx) * this.moveRange, y: 0 },
          { x: 0, y: Math.sign(dy) * this.moveRange }
        );
      } else {
        // Prioritize vertical movement
        directionPriority.push(
          { x: 0, y: Math.sign(dy) * this.moveRange },
          { x: Math.sign(dx) * this.moveRange, y: 0 }
        );
      }

      // Try each direction in priority order
      for (const dir of directionPriority) {
        const newX = this.gridX + dir.x;
        const newY = this.gridY + dir.y;

        // Check if the final position is valid (imps can hop over obstacles)
        if (newX >= 0 && newX < GRID_WIDTH && newY >= 0 && newY < GRID_HEIGHT) {
          // Check if landing spot is clear (we can hop over obstacles but need a clear landing)
          if (this.grid.isValidMove(newX, newY)) {
            return this.createMoveAction(newX, newY);
          }
        }
      }
    }

    // If no clear direction, move randomly
    const directions = [
      { x: this.moveRange, y: 0 },
      { x: -this.moveRange, y: 0 },
      { x: 0, y: this.moveRange },
      { x: 0, y: -this.moveRange }
    ];

    // Shuffle directions for randomness
    for (let i = directions.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [directions[i], directions[j]] = [directions[j], directions[i]];
    }

    // Try each direction
    for (const dir of directions) {
      const newX = this.gridX + dir.x;
      const newY = this.gridY + dir.y;

      if (this.grid.isValidMove(newX, newY)) {
        return this.createMoveAction(newX, newY);
      }
    }

    // If no move is possible, wait
    return this.createWaitAction();
  }
}